/**
 * Dashboard view / tab tracking.
 *
 * Every dashboard sends events to the existing `event_tracking` API through
 * `useAddEvent` (sent on dev and prod via its allEnvs option). The backend schema is fixed
 * (event, email, first_name, user_id, role_id, team, designation, case_id) and
 * Mongoose drops unknown fields, so WHERE the user was is encoded in the
 * `event` string itself:
 *
 *   db_<dashboard>                    page opened
 *   db_<dashboard>_<tab>              tab clicked
 *   db_<dashboard>_<tab>_<sub-tab>    sub-tab clicked (and so on)
 *
 * `_` separates levels, `-` joins words inside a level, so splitting on `_`
 * always gives [db, dashboard, tab, sub-tab, ...].
 *
 * Page views and URL-driven tabs (?tab= / &subTab=) are tracked centrally by
 * `useDashboardTracking` (mounted once in routes/index.js) for EVERY route in the
 * sidebar config (config/routeTabConfig.js), so new routes/tabs are covered
 * automatically. Hand-picked short keys in DASHBOARD_ROUTE_KEYS win; any other
 * sidebar route gets its path as the key (/order-management/update_order ->
 * order-management-update-order). Tabs that live only in component state call
 * `trackDashboardTab(...)` from their onChange.
 *
 * Full catalog: docs/DASHBOARD_EVENT_TRACKING.md
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAddEvent, buildEventPayload } from './store';
import { convertConfigToPermissions } from 'config/routeTabConfig';

export const DASHBOARD_EVENT_PREFIX = 'db';

/** Route path → short dashboard key. Alias routes of one dashboard share a key. */
export const DASHBOARD_ROUTE_KEYS = {
  // Main / home
  '/dashboard': 'home-main',
  '/dashboard/home': 'home',
  '/dashboard/revenue-report': 'revenue-report',
  // Lists / work queues under /dashboard (page view only; actionable sub-tab clicks
  // redirect to /dashboard?tab=actionable&subTab=… and are tracked there)
  '/dashboard/home/assigned-orders': 'assigned-orders',
  '/dashboard/actionables': 'actionables',
  '/dashboard/engagement': 'engagement',
  '/home': 'doctors',
  '/customer-data': 'customer-data',

  // Sales report
  '/sales-report/new-sales-charts': 'new-sales',
  '/new-sales-dashboard': 'new-sales',
  '/sales-report/sales-charts': 'sales-charts',
  '/financial-dashboard': 'sales-charts',
  '/sales-report/report-charts': 'report-charts',
  '/report-dashboard': 'report-charts',
  '/sales-report/month-report': 'month-report',
  '/mtd-report': 'month-report',
  '/sales-report/detailed-monthly-report': 'monthly-detailed',
  '/detailed-monthly-report': 'monthly-detailed',
  '/sales-report/sales-data': 'sales-data',
  '/sales-dashboard': 'sales-data',
  '/sales-report/utm-campaign': 'utm',
  '/sales-report/revenue-tracking': 'revenue-tracking',
  '/revenue-tracking': 'revenue-tracking',
  '/sales-report/transactions-dashboard': 'payment-gateway',
  '/transactions-dashboard': 'payment-gateway',
  '/sales-report/checkout-cart': 'checkout',
  '/checkout-success-rate': 'checkout',
  '/sales-report/web-funnel-dashboard': 'web-funnel',
  '/funnel-dashboard': 'web-funnel',
  '/sales-report/inventory-tracker': 'inventory',
  '/post-sales-dashboard': 'post-sales',

  // Operations
  '/reports': 'reports',
  '/operation-management/reports': 'reports',
  '/operation-management/uni-commerce': 'unicommerce',
  '/uni_commerce_dashboard': 'unicommerce',
  '/operation-management/control-tower': 'control-tower',
  '/control-tower': 'control-tower',
  '/operation-management/mool-inventory-tracker': 'mool-inventory',
  '/operation-management/acne-inventory-tracker': 'acne-inventory',
  '/operation-management/detailed-sales-chart': 'sales-chart-detailed',
  '/refund-tracking': 'refunds',
  '/operation-management/refund-tracking': 'refunds',
  '/order-dashboard': 'orders',
  '/dispatch-dashboard': 'dispatch',
  '/delivery-journey': 'delivery-journey',
  '/pg-reconciliation': 'pg-recon',

  // Admin / management
  '/admin-dashboard': 'admin',
  '/performance-dashboard': 'performance',
  '/productivity-dashboard': 'productivity',
  '/product-management': 'products',
  '/ssot': 'ssot',
  '/creator-dashboard': 'creator',

  // KPI
  '/kpi-dashboard': 'kpi-calling',
  '/kpi-dashboard/kpi-calling': 'kpi-calling',
  '/kpi-calling': 'kpi-calling',
  '/kpi-dashboard/kpi-historical': 'kpi-history',
  '/kpi-historical-dashboard': 'kpi-history',
  '/kpi-dashboard/medical-kpi': 'kpi-medical',
  '/medical-kpi': 'kpi-medical',
  '/kpi-dashboard/complaint-kpi': 'kpi-complaint',
  '/complaint-kpi': 'kpi-complaint',
  '/kpi-dashboard/call-attempts': 'call-attempts',
  '/manager-call-attempts': 'call-attempts',
  '/kpi-dashboard/call-transfer-audit': 'call-transfer-audit',
  '/call-transfer-audit': 'call-transfer-audit',
  '/kpi-dashboard/l2-ticketing-call-transfer': 'l2-audit',
  '/l2-ticketing-call-transfer': 'l2-audit',
  '/kpi-dashboard/recording-reconciliation': 'rec-recon',
  '/kpi-dashboard/recording-reconciliation-agent': 'rec-recon-agent',

  // Engagement
  '/engagement-report': 'engagement-reports',
  '/engagement-report/summary': 'engagement-summary',
  '/lead-dashboard': 'lead',
  '/engagement-dashboards/outbound-summary': 'outbound',
  '/engagement-dashboards/engagement-details': 'engagement-analytics',
  '/engagement-dashboards/slot-pendency': 'slot-pendency',
  '/engagement-dashboards/daily-pendency': 'pendency',
  '/engagement-dashboards/agent-login-report': 'agent-login',
  '/engagement-dashboards/agent-performance': 'agent-perf',
  '/engagement-dashboards/agent-performance-due': 'agent-perf-due',
  '/engagement-dashboards/lead-call-report': 'lead-calls',
  '/engagement-dashboards/same-coach': 'same-coach',
  '/engagement-dashboards/slot-availability': 'slot-availability',
  '/engagement-dashboards/slot-queue-pendency': 'slot-queue',
  '/engagement-dashboards/cold-call-queue-pendency': 'cold-queue',

  // Calls / communication
  '/call-dashboard': 'calls',
  '/call-stats': 'call-stats',
  '/covin-dashboard': 'convin',
  '/whatsapp-analytics': 'whatsapp',
  '/voice-bot-analytics': 'voicebot',
  '/smart-assist-dashboard': 'smart-assist',

  // Tickets
  '/ticket-master': 'ticket-master',
  '/ticketing-dashboard': 'ticketing',

  // Customer / case
  '/case-management/risky-customers': 'risky-customers',
  '/case-management/frustration-analytics': 'frustration',
  '/case-summary-v2': 'case-summary',
  '/case-management/slot-booking': 'case-slot-booking',
  '/funnel-analytics': 'funnel',

  // NPS / CSAT
  '/nps-dashboard/nps-analysis': 'nps',
  '/nps-dashboard/nps-v2': 'nps-v2',
  '/nps-dashboard/csat-summary': 'csat',

  // QA & audit
  '/qa-audit': 'qa-auto',
  '/qa-audit/evaluations': 'qa-auto',
  '/qa-audit/manual-qa': 'qa-manual',
  '/qa-audit/convin-audit': 'convin-audit',
  '/location-view': 'qa-location',
  '/coach-view': 'qa-coach',
  '/qa-view': 'qa-view',

  // Incentives
  '/incentives/calling-incentives': 'inc-calling',
  '/incentives-calculator': 'inc-calling',
  '/incentives/medical-incentives': 'inc-medical',
  '/incentives/complaints-incentives': 'inc-complaint',
  '/incentives-achievedScore': 'inc-score',

  // Clinic / offline
  '/clinic-dashboard': 'clinic',
  '/clinic-home': 'store-orders',
  '/store-visit': 'store-visit',
  '/offline/subscription-analytics': 'subscription',
  '/offline/grooming-audits': 'grooming',
  '/offline/checklist-reports': 'checklist',
  '/offline/offline-metrics': 'offline-metrics',

  // Work tools / borderline pages (tracked for whole-CRM usage)
  '/attendance/attendance-tracker': 'attendance',
  '/attendance-tracker': 'attendance',
  '/attendance/leave-tracker': 'leave-tracker',
  '/leave-tracker': 'leave-tracker',
  '/attendance/approvals-tracker': 'approvals-tracker',
  '/approvals-tracker': 'approvals-tracker',
  '/incentives-slab': 'inc-slab',
  '/weightage-target': 'weightage-target',
  '/idealtimecalculator': 'ideal-time',
  '/qa-audit/convin-dispatch': 'convin-dispatch',
  '/call-transcripts': 'call-transcripts',
  '/receptioninst-dashboard': 'receptionist',
  '/ask-ai': 'ask-ai',

  // Operational pages with report tabs (names already agreed; other sidebar pages get auto keys)
  '/warehouse': 'warehouse',
  '/operation-management/warehouse': 'warehouse',
  '/invoice': 'invoice',
  '/operation-management/invoice': 'invoice',
  '/feedback': 'feedback',
  '/user-management': 'user-management',
};

/**
 * Raw ?tab= / &subTab= value → short event segment, per route key. Unmapped
 * values fall back to slugify().
 */
export const URL_TAB_ALIASES = {
  'customer-data': { cohort: 'retention-cohort' },
  'home-main': { consultCall: 'consult-call', openOrder: 'open-order' },
  // The menu links ?tab=overview but the page's own key for that tab is "dashboard".
  whatsapp: { dashboard: 'overview' },
  'engagement-reports': {
    ivr_booking: 'ivr',
    week_1: 'week1',
    week_3: 'week3',
    adhoc_call: 'adhoc',
    concern_call: 'concern',
    scalpie_update: 'scalpie',
    customer_complaint_booking: 'complaint-booking',
    chat_slot_booking: 'chat-slot',
    w0_call: 'w0',
  },
  invoice: { download: 'invoice-report', downloadCaseOrderCount: 'case-order-count-report' },
};

/**
 * Pages whose tabs are kept in component state and tracked by trackDashboardTab
 * in their onChange. The sidebar may still link them with ?tab=, which the page
 * ignores, so URL tabs are NOT tracked for these (avoids wrong/double events).
 */
const INTERNAL_TAB_KEYS = new Set([
  'reports',
  'sales-charts',
  'checkout',
  'qa-manual',
  'admin',
  'user-management',
  'community',
  // sidebar pages whose config lists ?tab= links that the page itself ignores
  'configure-clinic-management',
  'configure-ticket-group-management',
  'ticket-group-management',
  'order-management-update-order',
  'admin-order-management-update-orders',
  'configure-ticket-configure',
  'manage-master-table',
  'doctor-booking-booking', // sidebar lists list/calendar views; the page has no tab bar
]);

/** Lowercase, words joined by '-' — never contains '_' (the level separator). */
export const slugify = (value) =>
  String(value ?? '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/→/g, '-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// The router matches paths case-insensitively (dynamicRouter lowercases them), so
// lookups are lowercase too: /incentives-achievedscore must still resolve.
const BASENAME = (process.env.PUBLIC_URL || '').replace(/\/+$/, '').toLowerCase();
const normalizePath = (pathname = '') => {
  let path = String(pathname).split('?')[0].toLowerCase();
  if (BASENAME && path.startsWith(BASENAME)) path = path.slice(BASENAME.length) || '/';
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
};
const lowerKeys = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));
const ROUTE_KEYS_LOWER = lowerKeys(DASHBOARD_ROUTE_KEYS);

/**
 * Auto key from a path: /admin-order-management/all-orders -> admin-order-management-all-orders.
 * :params are dropped and the key gets "-detail", so /next-call/:id (next-call-detail)
 * stays distinct from the /next-call list.
 */
const pathToKey = (path) => {
  const segments = path.split('/').filter(Boolean);
  const key = slugify(segments.filter((s) => !s.startsWith(':')).join('-'));
  return segments.some((s) => s.startsWith(':')) ? `${key}-detail` : key;
};
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Every route in the sidebar config, built lazily on first use.
let sidebarRoutes = null;
const getSidebarRoutes = () => {
  if (sidebarRoutes) return sidebarRoutes;
  const exact = {};
  const patterns = [];
  const add = (route) => {
    // Group headers (/attendance, /case-management, …) have no page of their own.
    if (!route?.componentPath) return;
    const path = normalizePath(route.path || '');
    if (!path || path === '/') return;
    if (path.includes(':')) {
      const re = new RegExp(`^${path.split('/').map((s) => (s.startsWith(':') ? '[^/]+' : escapeRegex(s))).join('/')}$`);
      patterns.push({ re, key: pathToKey(path) });
    } else {
      exact[path] = pathToKey(path);
    }
  };
  try {
    Object.values(convertConfigToPermissions() || {}).forEach((group) =>
      Object.values(group?.routes || {}).forEach((route) => {
        add(route);
        Object.values(route?.subRoutes || {}).forEach(add);
      })
    );
  } catch (error) {
    console.error('dashboard tracking: could not read sidebar routes', error);
  }
  sidebarRoutes = { exact, patterns };
  return sidebarRoutes;
};

/** Event key for a route: hand-picked short key, else sidebar route key, else null (untracked). */
export const getDashboardKey = (pathname) => {
  const path = normalizePath(pathname);
  if (ROUTE_KEYS_LOWER[path]) return ROUTE_KEYS_LOWER[path];
  const { exact, patterns } = getSidebarRoutes();
  if (exact[path]) return exact[path];
  const match = patterns.find(({ re }) => re.test(path));
  return match ? match.key : null;
};

/** db_<dashboard>[_<tab>[_<sub-tab>...]] — empty parts are skipped. */
export const buildDashboardEvent = (dashboardKey, ...parts) =>
  [DASHBOARD_EVENT_PREFIX, dashboardKey, ...parts.map(slugify)].filter(Boolean).join('_');

/**
 * Fire-and-forget. Tracking must never break a tab switch or page render.
 * allEnvs: dashboard events are sent on dev as well as prod (other events stay prod-only).
 */
export const trackDashboardEvent = (event) => {
  try {
    useAddEvent.getState().addEventData(buildEventPayload(event), { allEnvs: true });
  } catch (error) {
    console.error('dashboard event tracking failed', error);
  }
};

/**
 * For tabs that are NOT in the URL. The page is resolved from the current
 * route, so a component shared by two pages (e.g. SalesDashboard inside
 * /admin-dashboard) is still attributed to the page the user is on.
 *
 *   trackDashboardTab('trends')                          -> db_nps_trends
 *   trackDashboardTab('reorder-cohort', 'heatmap')       -> db_customer-data_reorder-cohort_heatmap
 */
export const trackDashboardTab = (...parts) => {
  try {
    const pathname = normalizePath(window.location.pathname);
    const dashboardKey = getDashboardKey(pathname) || pathToKey(pathname) || 'unknown';
    trackDashboardEvent(buildDashboardEvent(dashboardKey, ...parts));
  } catch (error) {
    console.error('dashboard tab tracking failed', error);
  }
};

const urlTabSegment = (dashboardKey, raw) => {
  const aliases = URL_TAB_ALIASES[dashboardKey] || {};
  return aliases[raw] || slugify(raw);
};

/**
 * Mount once inside the Router. For every sidebar route it fires:
 *  - db_<key>                 when the user lands on the route
 *  - db_<key>_<tab>[_<sub>]   when ?tab= / &subTab= (or &subtab=) changes
 * Other query changes (dates, page numbers, filters) do not fire anything.
 */
export function useDashboardTracking() {
  const { pathname, search } = useLocation();
  const last = useRef({ path: null, tabEvent: null });

  useEffect(() => {
    // Wrapped so a tracking bug can never take down routing (this hook runs on every page).
    const track = () => {
      const path = normalizePath(pathname);
      const key = getDashboardKey(path);

      if (!key) {
        last.current = { path: null, tabEvent: null };
        return;
      }

      if (last.current.path !== path) {
        last.current = { path, tabEvent: null };
        trackDashboardEvent(buildDashboardEvent(key));
      }

      if (INTERNAL_TAB_KEYS.has(key)) return;

      const params = new URLSearchParams(search);
      const tab = params.get('tab');
      if (!tab) return;

      const subTab = params.get('subTab') || params.get('subtab');
      const event = buildDashboardEvent(key, urlTabSegment(key, tab), subTab ? urlTabSegment(key, subTab) : null);

      if (last.current.tabEvent !== event) {
        last.current.tabEvent = event;
        trackDashboardEvent(event);
      }
    };
    try {
      track();
    } catch (error) {
      console.error('dashboard route tracking failed', error);
    }
  }, [pathname, search]);
}
